<script setup lang="ts">
import type { SuggestionCluster } from '@/components/domain/record-link/fields';
import { computed, ref, watch } from 'vue';
import { SphCheckbox } from '@/components/base/checkbox';
import { SphDialog } from '@/components/base/dialog';
import { useI18n } from '@/composables/i18n';
import RecordDifferences from './RecordDifferences.vue';

defineOptions({
    compatConfig: { MODE: 3 },
});

const props = defineProps<{
    cluster: SuggestionCluster
    name: string
}>();

const emit = defineEmits<{
    submit: [payload: LinkRecordsSubmit]
}>();

export interface LinkRecordsSubmit {
    memberIds: number[]
    done: (_success: boolean) => void
}

const { i18n } = useI18n();

const open = defineModel('state', { type: Boolean, default: false });

// a same-club double row blocks the whole cluster: the club has to clean it up before anything here can link
const blockedByDuplicate = computed(() => props.cluster.duplicateInstitutionNames.length > 0);
const duplicateClubsLine = computed(() => {
    const clubs = props.cluster.duplicateInstitutionNames.join(', ');

    return props.cluster.duplicateInstitutionNames.length === 1
        ? i18n.t('Im Verein {clubs} liegen mehrere Datensätze zu dieser Person vor.', { clubs })
        : i18n.t('In den Vereinen {clubs} liegen mehrere Datensätze zu dieser Person vor.', { clubs });
});

const selectedIds = ref<number[]>([]);

// every record starts selected: the dialog exists to opt records out, not in
watch(open, (isOpen) => {
    if (isOpen) {
        selectedIds.value = props.cluster.members.map((member) => member.id);
    }
});

const isSelected = (id: number) => selectedIds.value.includes(id);

function toggle(id: number) {
    selectedIds.value = isSelected(id)
        ? selectedIds.value.filter((selectedId) => selectedId !== id)
        : [...selectedIds.value, id];
}

function onSubmit(event: { done: (_success: boolean) => void }) {
    emit('submit', { memberIds: [...selectedIds.value], done: event.done });
}
</script>

<template>
    <SphDialog
        v-model:state="open"
        variant="form"
        :title="$t('{name} verknüpfen', { name })"
        :submit-button-label="`${$t('Verknüpfen')} (${selectedIds.length})`"
        :disable-submit="selectedIds.length < 2 || blockedByDuplicate"
        @submit="onSubmit"
    >
        <p>{{ $t('{count} Datensätze gefunden. Prüfen Sie die Abweichungen und wählen Sie, welche Datensätze verknüpft werden sollen.', { count: cluster.members.length }) }}</p>

        <div
            v-if="blockedByDuplicate"
            class="kgr-alert kgr-alert--warning mt-4"
        >
            {{ duplicateClubsLine }}
            {{ $t('Solche Dubletten müssen zuerst im Verein bereinigt werden, bevor die Datensätze verknüpft werden können.') }}
        </div>

        <RecordDifferences
            :records="cluster.members"
            :fields="cluster.fields"
            :divergent-fields="cluster.divergentFields"
            class="my-4"
        />

        <h4 class="kgr-headline kgr-headline--h4">
            {{ $t('Zu verknüpfende Datensätze') }}
        </h4>
        <table class="kgr-table kgr-table--striped w-full">
            <tbody>
                <tr
                    v-for="member in cluster.members"
                    :key="member.id"
                    class="kgr-table__row"
                >
                    <td class="kgr-table__cell kgr-table__cell--noGrow">
                        <SphCheckbox
                            :model-value="isSelected(member.id)"
                            @update:model-value="toggle(member.id)"
                        />
                    </td>
                    <td class="kgr-table__cell">
                        <div>{{ member.institutionName }}</div>
                        <div class="kgr-text kgr-text--light">
                            {{ member.displayName }}
                        </div>
                    </td>
                </tr>
            </tbody>
        </table>
        <p class="kgr-text kgr-text--light">
            {{ $t('Nicht ausgewählte Datensätze bleiben als eigener Vorschlag offen.') }}
        </p>
    </SphDialog>
</template>
